import type { CityConfig } from "@/pages/city-template/types";

export const johnstownConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Johnstown",
  state: "Pennsylvania",
  stateAbbr: "PA",
  slug: "johnstown",
  route: "/johnstown",
  title: "Emergency Passport Johnstown, PA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Johnstown, PA — as fast as 24 hours. Same-day courier processing available for Cambria County travelers, UPJ students, and Western Pennsylvania residents. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Johnstown, Pennsylvania",
  heroSubtitle: "Planning a trip from Johnstown and your passport isn't ready? Studying at UPJ with a study abroad deadline? Fast Passport Center helps Johnstown and Cambria County residents get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Johnstown%20Pennsylvania%20historic%20downtown%20with%20classic%20brick%20buildings%20and%20the%20iconic%20Stone%20Bridge%20across%20Conemaugh%20River%2C%20rolling%20Allegheny%20Mountain%20foothills%20in%20background%2C%20Cambria%20County%20landscape%20with%20autumn%20colors%2C%20small%20industrial%20heritage%20city%20atmosphere%2C%20warm%20afternoon%20golden%20light%2C%20photorealistic%20aerial%20neighborhood%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=johnstown-hero-01&orientation=landscape",
  heroImageAlt: "Johnstown Pennsylvania historic downtown with Stone Bridge and Conemaugh River valley",
  geoPosition: "40.3267;-78.9220",
  geoPlacename: "Johnstown, Pennsylvania",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "200 Chestnut St, Philadelphia, PA 19106",
    distance: "About 260 miles east — 4.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Johnstown", zips: "15901, 15902, 15904, 15905, 15906" },
    { name: "Westmont", zips: "15905" },
    { name: "Richland", zips: "15904" },
    { name: "Windber", zips: "15963" },
    { name: "Somerset", zips: "15501" },
    { name: "Ebensburg", zips: "15931" },
    { name: "Nanty Glo", zips: "15943" },
    { name: "Portage", zips: "15946" },
    { name: "Cresson", zips: "16630" },
    { name: "Davidsville", zips: "15928" },
    { name: "Geistown", zips: "15904" },
    { name: "Southmont", zips: "15905" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Johnstown or your dorm at Pitt-Johnstown in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Cambria County", desc: "FedEx and UPS locations throughout Johnstown and the Cambria County area offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — critical for UPJ students with tight study abroad deadlines." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Philadelphia Passport Agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "David M.",
      location: "Johnstown, PA",
      rating: 5,
      text: "I needed a passport renewal for a business trip to Toronto on two weeks' notice. Fast Passport Center handled everything. Shipped from the FedEx in Richland and had my new passport in 6 days. Couldn't believe how smooth it was.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa R.",
      location: "Westmont, PA",
      rating: 5,
      text: "Our daughter is doing a semester abroad through UPJ and we realized her passport was expired just three weeks before departure. Fast Passport Center got it renewed in 5 days. The checklist made everything so clear.",
      service: "New Passport",
    },
    {
      name: "Tom H.",
      location: "Windber, PA",
      rating: 5,
      text: "Family reunion in Ireland coming up and both kids needed first passports. Fast Passport Center walked us through the child passport process step by step. Both arrived in 8 days. Fantastic service.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "johnstown-post",
      name: "Johnstown Post Office",
      type: "USPS Passport Acceptance",
      address: "111 Franklin St",
      city: "Johnstown",
      state: "PA",
      zip: "15901",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Johnstown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Johnstown. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "ebensburg-post",
      name: "Ebensburg Post Office",
      type: "USPS Passport Acceptance",
      address: "115 W Highland Ave",
      city: "Ebensburg",
      state: "PA",
      zip: "15931",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Ebensburg — about 15 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Ebensburg option for northern Cambria County residents. County seat location.",
      icon: "ri-mail-line",
    },
    {
      id: "somerset-post",
      name: "Somerset Post Office",
      type: "USPS Passport Acceptance",
      address: "201 N Center Ave",
      city: "Somerset",
      state: "PA",
      zip: "15501",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Somerset — about 25 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Somerset option for southern Cambria County and Somerset County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "cambria-county-courthouse",
      name: "Cambria County Courthouse",
      type: "County Passport Acceptance",
      address: "200 S Center St",
      city: "Ebensburg",
      state: "PA",
      zip: "15931",
      phone: "814-472-1440",
      hours: "Mon–Fri 8:30am–4:30pm",
      distance: "Ebensburg — about 15 miles north",
      website: "https://www.cambriacountypa.gov/",
      websiteLabel: "Cambria County Website",
      notes: "County courthouse passport acceptance. Call ahead to confirm hours and appointment requirements.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Johnstown before international travel?",
      a: "Yes. Through Fast Passport Center, Johnstown travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Johnstown?",
      a: "Fast passport renewal in Johnstown is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years.",
    },
    {
      q: "Can UPJ students get expedited passports for study abroad?",
      a: "Absolutely. We help University of Pittsburgh at Johnstown students get passports fast for study abroad programs. Whether it's a first-time passport or a renewal, we guide you through the process and can meet even tight semester deadlines.",
    },
    {
      q: "Does Johnstown have a regional passport agency?",
      a: "Johnstown does not have a regional passport agency. The closest is the Philadelphia Passport Agency (about 260 miles east). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution for most Western Pennsylvania residents.",
    },
    {
      q: "Can I expedite a child passport in Johnstown?",
      a: "Yes. Child passport expediting in Johnstown is possible through Fast Passport Center. Child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist. Once the acceptance step is complete, we handle expedited processing.",
    },
    {
      q: "What airports do Johnstown travelers typically use?",
      a: "John Murtha Johnstown-Cambria County Airport (JST) serves the immediate area with regional connections. For commercial flights, Pittsburgh International Airport (PIT — about 80 miles west) is the primary airport for Johnstown travelers. Both offer domestic connections for international flights.",
    },
    {
      q: "How long does a standard passport take in Johnstown?",
      a: "If you apply through a standard post office acceptance facility in Johnstown, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "Can I get a passport renewal in Johnstown without visiting an office?",
      a: "Yes. Most Johnstown-area adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online, complete documents at home, and ship overnight from any FedEx or UPS location in Johnstown or the Cambria County area.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Johnstown", address: "1451 Scalp Ave, Johnstown, PA 15904", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Johnstown", address: "425 Main St, Johnstown, PA 15901", hours: "Daily 8 AM – 9 PM" },
    { name: "Walmart — Johnstown", address: "150 Town Centre Dr, Johnstown, PA 15904", hours: "Daily 6 AM – 11 PM" },
    { name: "The UPS Store — Richland", address: "3436 William Penn Ave, Johnstown, PA 15909", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 3 PM" },
    { name: "FedEx Office — Altoona", address: "3415 Pleasant Valley Blvd, Altoona, PA 16602", hours: "Mon–Fri 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Johnstown PennDOT Driver License Center",
      address: "563 Walters Ave, Johnstown, PA 15904",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Johnstown PennDOT center. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Altoona PennDOT Driver License Center",
      address: "1738 A Union Ave, Altoona, PA 16602",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Altoona PennDOT option for eastern Cambria County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "JST",
      name: "John Murtha Johnstown-Cambria County Airport",
      distance: "About 4 miles east of downtown Johnstown",
      address: "479 Airport Rd, Johnstown, PA 15904",
      note: "JST offers regional connections to major hubs. For international travel, connections through Pittsburgh or other major airports. Passport required for all international connections.",
      flightUrl: "https://www.flyjst.com/",
      flightLabel: "JST Airport Website",
      featured: true,
    },
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "About 80 miles west — 1.5 hours drive",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "PIT is the primary commercial airport for Johnstown area travelers, offering extensive domestic and international connections.",
      flightUrl: "https://flypittsburgh.com/",
      flightLabel: "PIT Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PIT or JST in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Johnstown and Cambria County.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Johnstown and Cambria County travelers renew fast — no office visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Johnstown", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Johnstown travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Johnstown families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Johnstown residents and Pitt-Johnstown students.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all expedited.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Johnstown, Pennsylvania travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Johnstown and Cambria County residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Johnstown, PA residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Johnstown travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Johnstown families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Johnstown, Pennsylvania." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Johnstown travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. We tell you exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Johnstown runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Johnstown travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Johnstown", description: "Drop off at any FedEx or UPS in the Johnstown or Cambria County area. Most locations are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins", "Life-or-death emergencies only", "Must travel to Philadelphia (4.5 hours)", "Expect to wait all day"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Philadelphia", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Philadelphia (4.5 hours each way)", fpc: "Ship documents overnight from Johnstown" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Door-to-Door", agency: "You drive 4.5 hours and wait all day", fpc: "Ship from local FedEx/UPS — we handle the rest" },
    { feature: "Predictability", agency: "Appointments can be cancelled", fpc: "Predictable processing based on option" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Success Rate", agency: "Depends on appointment availability", fpc: "99%+ application approval rate" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Pittsburgh", route: "/pittsburgh" },
    { name: "Fast passport renewal in Altoona", route: "/altoona" },
    { name: "Emergency passport help near Cambria County", route: "/pennsylvania" },
    { name: "Rush passport service for Johnstown travelers", route: "/pennsylvania" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Johnstown or the Cambria County area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Johnstown and all of Cambria County remotely",
  serviceAreaIntro: "Whether you are in downtown Johnstown, Westmont, Richland, Windber, or anywhere in Cambria County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Johnstown and Cambria County zip codes. Anywhere in the Laurel Highlands — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Johnstown as simple as possible. Most Johnstown clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Johnstown travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Johnstown, Westmont, and Windber travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Johnstown and Cambria County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/pennsylvania",
  statePageName: "Pennsylvania",
  dmvWebsite: "https://www.dmv.pa.gov/",
  dmvLabel: "PennDOT — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Pennsylvania Department of Health Vital Records for Cambria County.", url: "https://www.health.pa.gov/topics/certificates/Pages/Birth-Certificates.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Pennsylvania Department of Health Vital Records.", url: "https://www.health.pa.gov/topics/certificates/Pages/Death-Certificates.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Cambria County Register of Wills.", url: "https://www.cambriacountypa.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Cambria County Prothonotary.", url: "https://www.cambriacountypa.gov/" },
    { icon: "ri-global-line", label: "VitalChek", desc: "Order Pennsylvania birth, death, marriage, and divorce certificates online through VitalChek for convenience.", url: "https://www.vitalchek.com/" },
  ],
};